/**
 * Copyright: ThoughtSpot Inc. 2020
 * Author: Thawsitt Naing (thawsitt.naing@thoughtspot.com)
 *
 * @fileoverview Toast Alert for Radiant
 */
import { blinkConstants } from '@thoughtspot/blink-constants';
import { useEmbedContext } from '@thoughtspot/blink-context';
import cx from 'classnames';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Button } from '../../button';
import { Icon, IconColor, IconID, IconSize } from '../../icon';
import { LoaderContextual } from '../../loading-indicator/loader-contextual';
import { Typography } from '../../typography';
import { CLOSE_BTN_ARIA_LABEL, TOAST_HIDE_DELAY_MS } from '../alert-constants';
import { createActionLink } from '../alert-utils';
import styles from './toast-alert.module.scss';
export const ToastAlert = ({ message, isVisible, primaryAction, link, showCheckmarkIcon = true, showLoadingIcon = false, showCloseButton = true, onClose = _.noop, autoHide = true, isCustom = false, className = '', shouldRefreshToastAnimation = true, shouldAnnounceToastAlert = false, toastMessageRef, ...restProps }) => {
    const actionLink = link ? createActionLink(link, styles.link) : null;
    const { embedParams, isEmbedded, dispatchEmbedEvent } = useEmbedContext();
    const [embedClass, setEmbedClass] = useState({});
    const showAlerts = isEmbedded && embedParams.showAlerts !== undefined
        ? !!embedParams.showAlerts
        : true;
    const isFullHeightPinboard = embedParams === null || embedParams === void 0 ? void 0 : embedParams.isFullHeightPinboard;
    let animationClass = styles.noHide;
    if (autoHide) {
        animationClass = isFullHeightPinboard
            ? styles.autoHideFullheight
            : styles.autoHide;
    }
    const primaryActionButton = primaryAction ? (React.createElement(Button, { text: primaryAction.label, type: "primary", size: "s", backgroundType: "colored", className: styles.primaryAction, onClick: primaryAction.onClick, "data-testid": "manage-alert-button" })) : null;
    useEffect(() => {
        const timer = setTimeout(() => {
            if (autoHide && isVisible) {
                onClose();
            }
        }, TOAST_HIDE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [autoHide, isVisible, onClose]);
    useEffect(() => {
        // React elements can't be structured-cloned by postMessage
        if (isEmbedded && isVisible && typeof message === 'string') {
            dispatchEmbedEvent(blinkConstants.embedEvents.Alert, {
                alertMessage: message,
            });
        }
    }, [showAlerts, isVisible, isEmbedded, message, dispatchEmbedEvent]);
    useEffect(() => {
        const fetchEmbedModalCenter = async () => {
            if (isFullHeightPinboard) {
                const bufferHeight = 20;
                const embedModalCenter = await dispatchEmbedEvent(blinkConstants.embedEvents.EmbedIframeCenter, {});
                const top = Math.max(0, embedModalCenter.data.iframeScrolled);
                const bottom = embedModalCenter.data.iframeHeight -
                    embedModalCenter.data.iframeVisibleViewPort -
                    top;
                const iframeHeight = embedModalCenter.data.iframeHeight;
                const style = {
                    marginBottom: bottom + bufferHeight,
                    '--iframe-height': `${iframeHeight}px`,
                };
                setEmbedClass(style);
            }
        };
        fetchEmbedModalCenter();
    }, [isFullHeightPinboard, dispatchEmbedEvent, isVisible]);
    const containerClass = cx(className, styles.container, animationClass);
    if (!isVisible || !message || !showAlerts) {
        return null;
    }
    return (React.createElement("div", { className: containerClass, ...restProps, style: isFullHeightPinboard ? embedClass : {} },
        showLoadingIcon ? (React.createElement(LoaderContextual, { loaderColor: "white", className: styles.icon })) : (showCheckmarkIcon && (React.createElement(Icon, { id: IconID.CHECKMARK_CIRCLE, size: IconSize.LARGE, color: IconColor.WHITE, className: styles.icon }))),
        React.createElement("div", { className: styles.content, ...(shouldAnnounceToastAlert
                ? {
                    role: 'alert',
                    'aria-live': 'assertive',
                    tabIndex: -1,
                }
                : {}), ref: toastMessageRef },
            isCustom ? (React.createElement(React.Fragment, null, message)) : (React.createElement(Typography, { variant: "body-normal", className: styles.message }, message)),
            actionLink,
            primaryActionButton),
        showCloseButton && (React.createElement(Button, { size: "s", icon: IconID.CROSS, type: "tertiary", backgroundType: "colored", ariaLabel: CLOSE_BTN_ARIA_LABEL, onClick: onClose, className: styles.closeButton }))));
};
//# sourceMappingURL=toast-alert.js.map